import React, { useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import { Organization } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface DeleteOrganizationDialogProps {
  isOpen: boolean
  onClose: () => void
  organization: Pick<Organization, 'id' | 'name'> & { files_count?: number }
  onDeleted: () => void
}

export default function DeleteOrganizationDialog({
  isOpen,
  onClose,
  organization,
  onDeleted,
}: DeleteOrganizationDialogProps) {
  const { t } = useAppTranslation('admin')
  const toast = useToast()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const [confirmName, setConfirmName] = useState('')
  const [deleting, setDeleting] = useState(false)

  const nameMatches = confirmName.trim() === organization.name

  const handleClose = () => {
    setConfirmName('')
    onClose()
  }

  const handleDelete = async () => {
    if (!nameMatches) return
    setDeleting(true)
    try {
      await apiService.deleteAdminOrganization(organization.id)
      toast({
        title: t('deleteOrganization.toast.success'),
        description: t('deleteOrganization.toast.successDescription', {
          name: organization.name,
        }),
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      handleClose()
      onDeleted()
    } catch (error: any) {
      toast({
        title: t('deleteOrganization.toast.error'),
        description:
          error.response?.data?.error || t('deleteOrganization.toast.errorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setDeleting(false)
    }
  }

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={handleClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {t('deleteOrganization.title')}
          </AlertDialogHeader>

          <AlertDialogBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="error" borderRadius="md" fontSize="sm">
                <AlertIcon />
                {t('deleteOrganization.warning', {
                  count: organization.files_count ?? 0,
                })}
              </Alert>
              <Text fontSize="sm">
                {t('deleteOrganization.message', { name: organization.name })}
              </Text>
              <FormControl>
                <FormLabel fontSize="sm">
                  {t('deleteOrganization.typeNameToConfirm', { name: organization.name })}
                </FormLabel>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={organization.name}
                  autoFocus
                />
              </FormControl>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={handleClose}>
              {t('deleteOrganization.cancel')}
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              ml={3}
              isLoading={deleting}
              isDisabled={!nameMatches}
            >
              {t('deleteOrganization.confirm')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
